// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import useIsMobile from "../../hooks/useIsMobile";
import { ReactComponent as Table } from "../../assets/icons/layout-grid-02.svg";
import { ReactComponent as Graph } from "../../assets/icons/line-chart.svg";
import { TIME_RANGES } from "../forecastGraphs/graphutil";
import { useNavigate, useParams } from "react-router-dom";
import tzlookup from "tz-lookup";
import { loadAssetGraphSelection, saveAssetGraphSelection, saveAssetGraphExtras, saveAssetGraphOrder } from "../../util/assetGraphSelection";
import { AssetGraphService } from "./hooks/assetGraphService";
import { SkeletonChart } from "../Skeleton";
import AssetGraphTable from "./helpers/AssetGraphTable";
import SortableChartStack from "./helpers/SortableChartStack";
import StackedChartControls from "./helpers/StackedChartControls";
import ReactGA from "react-ga4";
import AssetGraphHeaderDesktop from "./helpers/AssetGraphHeaderDesktop";
import AssetGraphHeaderMobile from "./helpers/AssetGraphHeaderMobile";
import AssetGraphDashboardView from "./helpers/AssetGraphDashboardView";
import AssetGraphDisplay from "./helpers/AssetGraphDisplay";
import {
  buildForecastSchema,
  filterForecastSchema,
  getSmartDefaultForecast,
  getDefaultVariation,
  assetDefaultForecastKey,
  isWeatherHidden,
  hasAttachedForecasts,
} from "../utils/forecastSchemaUtils";
import { PageEmptyState } from "../AssetBreadCrumb";
import { fetchCombinedPreviewData, viewYAxisLabel } from "../../utils/composedForecastUtils";
import {
  resolveComposedForecast,
  flattenComposedForecast,
} from "../../utils/composedForecastDashboardUtils";
import {
  setAssetTimeZoneContext,
  clearAssetTimeZoneContext,
} from "../../util/timeZoneContext";
import {
  setActiveProcessedModel,
  clearActiveProcessedModel,
} from "../../store/assetsSlice";

// Move this, will also need it for energy model Labels were never rendered while the view switch was an icon-only dropdown, which is how the graph view ended up named after a forecast ("Wind (10m)"). The segmented control shows them, so they say what the view is.
const viewSchema = {
  // The old Hybrid view (graph with the map beneath) is the page's Graph view; the map drops away on its own where it can't show
  hybrid: {
    name: "Graph",
    Icon: Graph,
  },
  table: {
    name: "Table",
    Icon: Table,
  },
};

const AssetGraph = ({
  selectedAsset,
  disableMapView = false,
  hideHeader = false,
  onRegisterForecastControls,
  onRegisterGraphControls,
  onRegisterViewControls,
  onRegisterDownload,
  hideGraphControls = false,
  hideContainer = false,
  /** Host-owned control rendered at the leading edge of the control row, before the view switch. */
  leadingControl = null,
  /** Splits the control row onto its own card above a second card holding the graph. */
  headerBar = false,
  /** Panel rendered at the graph card's leading edge — a peek rail, so it sits inside that card. */
  graphLeadingPanel = null,
}) => {
  const { groupid } = useParams();
  const { assets } = useSelector(({ assets }) => ({ assets }));
  const navigate = useNavigate();
  const assetList = assets?.dataGroups?.[groupid]
  const dispatch = useDispatch();

  // The asset can hide every weather forecast type, leaving only its Custom Views and processed models
  const weatherHidden = isWeatherHidden(selectedAsset?.displayedChannels);
  // Weather hidden and nothing attached: there is no forecast to show, so the graph shows an empty state
  const noForecastsToShow = weatherHidden && !hasAttachedForecasts(selectedAsset);

  // The asset's own forecast: its default Custom View, else its first processed model (null when it has neither)
  const assetDefaultKey = assetDefaultForecastKey(selectedAsset);
  const [activeForecast, setActiveForecast] = useState(assetDefaultKey ?? (weatherHidden ? null : "Temperature"));

  // Further forecasts stacked under the main one (activeForecast). The main one keeps every existing behaviour — the
  // variation chips, time range, datasets, table and CSV all follow it — while each extra renders its own chart on its default variation.
  const [extraForecasts, setExtraForecasts] = useState<string[]>([]);
  // The user's order for the picker's channels, ticked or not. The stacked extras follow it; the main chart stays on top.
  const [channelOrder, setChannelOrder] = useState<string[]>([]);
  // Each stacked extra chart's own variation (the main chart's is forecastVariation), and the time-range / dataset openers each chart registers for its own toolbar
  const [extraVariations, setExtraVariations] = useState<Record<string, string>>({});
  const [controlsByKey, setControlsByKey] = useState<Record<string, any>>({});
  const registrarsRef = useRef<Record<string, (controls: any) => void>>({});
  const registrarFor = (key) => {
    if (!registrarsRef.current[key]) {
      registrarsRef.current[key] = (controls) => setControlsByKey((prev) => ({ ...prev, [key]: controls || null }));
    }
    return registrarsRef.current[key];
  };

  const [forecastVariation, setForecastVariation] = useState(
    assetDefaultKey?.startsWith("composed-") ? "Combined" : "Hourly"
  );

  // On an asset switch, open on the new asset's own forecast. With none, keep the user's weather type (the
  // auto-select effect below swaps it if the new asset doesn't offer it) but drop the previous asset's view or model.
  // This component isn't remounted per asset and the asset's links can arrive after mount, so this runs when the
  // asset or its default changes — and only then, so a forecast the user picks by hand isn't overridden on a refetch.
  // A processed model's variation is fixed up once its data loads (see the variation effect below).
  const appliedDefaultRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedAsset?.id) return;
    const marker = `${selectedAsset.id}:${assetDefaultKey ?? ""}`;
    if (appliedDefaultRef.current === marker) return;
    appliedDefaultRef.current = marker;
    // What the user last chose on this asset wins over the asset's default; a saved forecast the asset no longer offers is swapped by the auto-select effect below
    const saved = loadAssetGraphSelection(selectedAsset.id);
    setExtraForecasts(saved?.extras ?? []);
    setChannelOrder(saved?.order ?? []);
    setExtraVariations({});
    if (saved) {
      setActiveForecast(saved.forecast);
      setForecastVariation(saved.variation);
    } else if (assetDefaultKey) {
      setActiveForecast(assetDefaultKey);
      if (assetDefaultKey.startsWith("composed-")) setForecastVariation("Combined");
    } else {
      setActiveForecast((prev) =>
        prev?.startsWith("processed-") || prev?.startsWith("composed-") ? null : prev
      );
    }
  }, [selectedAsset?.id, assetDefaultKey]);
  const [wind10, setWind10] = useState(null);
  const [wind80, setWind80] = useState(null);
  const [precipitation, setPrecipitation] = useState(null);
  const [solarRadiation, setSolarRadiation] = useState(null);
  const [windPower80Data, setWindPower80Data] = useState(null);
  const [processedModelData, setProcessedModelData] = useState(null);
  // Cache of CombinedPreviewData per composed-forecast id. Populated lazily when the user selects a `composed-${id}` entry in the dropdown.
  const [composedForecastData, setComposedForecastData] = useState<Record<string, any>>({});
  const [additionalDataSources, setAdditionalDataSources] = useState(null);
  const [temperature2m, setTemperature2m] = useState(null)
  const [humidity2m, setHumidity2m] = useState(null)
  const [soilMoisture, setSoilMoisture] = useState(null)
  const [snowAcc, setSnowAcc] = useState(null)
  const [graphCastTemp, setGraphCastTemp] = useState(null)
  const [graphCastWind, setGraphCastWind] = useState(null)
  const [graphCastPrecip, setGraphCastPrecip] = useState(null)

  // High Res comes from same source, refactor?
  const [highResPrecip, setHighResPrecip] = useState(null);
  const [highResSolar, setHighResSolar] = useState(null);
  const [highResWind10, setHighResWind10] = useState(null);
  const [highResWind80, setHighResWind80] = useState(null);
  const [highResWindPower80Data, setHighResWindPower80Data] = useState(null);
  const [highResTemp, setHighResTemp] = useState(null);
  const [highResHumidity, setHighResHumidity] = useState(null)
  // const [highResSoilMoisture, setHighResSoilMoisture] = useState(null)

  const [dateTimeCycle, setDateTimeCycle] = useState(null);
  const parseCoordinate = useCallback((value) => {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === "string") {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    }
    return null;
  }, []);

  const normalizedLat = useMemo(
    () => parseCoordinate(selectedAsset?.lat),
    [selectedAsset?.lat, parseCoordinate]
  );
  const normalizedLng = useMemo(
    () => parseCoordinate(selectedAsset?.lng),
    [selectedAsset?.lng, parseCoordinate]
  );

  const assetTimeZone = useMemo(() => {
    if (selectedAsset?.timeZone) {
      return selectedAsset.timeZone;
    }
    if (
      typeof normalizedLat === "number" &&
      typeof normalizedLng === "number"
    ) {
      try {
        return tzlookup(normalizedLat, normalizedLng);
      } catch (err) {
        console.warn("Unable to resolve asset timezone:", err);
      }
    }
    return null;
  }, [selectedAsset?.timeZone, normalizedLat, normalizedLng]);

  const getLocalTimeZone = useCallback(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  }, []);

  const [timeZone, setTimeZone] = useState(
    assetTimeZone || getLocalTimeZone()
  );
  const [displayView, setDisplayView] = useState(() =>
    "hybrid"
  );
  const [solarEnergyInputs, setSolarEnergyInputs] = useState({
    thefw: null,
    ohhfc: null,
  });
  const [solarPowerData, setSolarPowerData] = useState(null);

  // Always on: the toggle that used to drive this is gone, and every consumer expects interpolation
  const interpolateChecked = true;
  const [graphControls, setGraphControls] = useState({ openTimeRange: null, openDataset: null, selectedTimeRange: null });
  const handleRegisterControls = useCallback((controls) => {
    setGraphControls(controls || { openTimeRange: null, openDataset: null, selectedTimeRange: null });
  }, []);

  useEffect(() => {
    if (!onRegisterGraphControls) return;
    onRegisterGraphControls(graphControls);
    return () => onRegisterGraphControls({ openTimeRange: null, openDataset: null, getCurrentRange: null });
  }, [onRegisterGraphControls, graphControls]);

  useEffect(() => {
    if (!onRegisterViewControls) return;
    onRegisterViewControls({
      displayView,
      setDisplayView,
    });
    return () => onRegisterViewControls(null);
  }, [onRegisterViewControls, displayView]);


  // The asset the latest fetches are for. The fetches below aren't cancelled on an asset switch, so a slow
  // response for the previous asset would otherwise land on this one — each drops its result if the asset moved on.
  const currentAssetIdRef = useRef(selectedAsset?.id);
  currentAssetIdRef.current = selectedAsset?.id;
  const isStaleFetch = (assetId) => currentAssetIdRef.current !== assetId;

  const getShortForecast = async () => {
    const assetId = selectedAsset?.id;
    const res = await AssetGraphService.fetchShortForecast(
      selectedAsset.lat,
      selectedAsset.lng
    );
    if (isStaleFetch(assetId)) return;
    const { precip, solar, wind, temp, humidity, solarEnergyWeatherDataInputs } =
      res;
    setHighResPrecip(precip);
    setHighResSolar(solar);
    setHighResWind10(wind.wind10);
    setHighResWind80(wind.wind80);
    setHighResTemp(temp);
    setHighResHumidity(humidity);
    // setHighResSoilMoisture(soilMoisture); //not ready yet
    setSolarEnergyInputs((state) => ({
      ...state,
      ohhfc: solarEnergyWeatherDataInputs,
    }));
  };

  // 0.5 resolution
  const getWind80 = async () => {
    const assetId = selectedAsset?.id;
    const wind = await AssetGraphService.fetchWind80(
      selectedAsset.lat,
      selectedAsset.lng
    );
    if (isStaleFetch(assetId)) return;
    setWind80(wind);
  };

  // 0.25 resolution
  const getForecasts = async () => {
    const assetId = selectedAsset?.id;
    const res = await AssetGraphService.fetchForecasts(
      selectedAsset.lat,
      selectedAsset.lng
    );
    if (isStaleFetch(assetId)) return;

    const {
      precip,
      solar,
      wind,
      temperature,
      humidity,
      soilMoisture,
      snowAcc,
      solarEnergyWeatherDataInputs,
      dateTimeCycle,
    } = res;

    setPrecipitation(precip);
    setWind10(wind);
    setSolarRadiation(solar);
    setTemperature2m(temperature);
    setHumidity2m(humidity);
    setSoilMoisture(soilMoisture);
    setSnowAcc(snowAcc);
    setSolarEnergyInputs((state) => ({
      ...state,
      thefw: solarEnergyWeatherDataInputs,
    }));
    setDateTimeCycle(dateTimeCycle);
  };

  useEffect(() => {
    const nextZone = assetTimeZone || getLocalTimeZone();
    setTimeZone(nextZone);
  }, [assetTimeZone, selectedAsset?.id, getLocalTimeZone]);

  useEffect(() => {
    const assetId = selectedAsset?.id;
    if (!assetId) {
      return;
    }

    const timeZoneMode: "local" | "asset" | "UTC" =
      timeZone === "UTC"
        ? "UTC"
        : assetTimeZone && timeZone === assetTimeZone
        ? "asset"
        : "local";

    setAssetTimeZoneContext(assetId, {
      assetTimeZone: assetTimeZone ?? null,
      timeZone,
      timeZoneMode,
    });

    return () => {
      clearAssetTimeZoneContext(assetId);
    };
  }, [selectedAsset?.id, assetTimeZone, timeZone]);

  // Sync active processed model to Redux so the Results modal can read it
  useEffect(() => {
    const assetId = selectedAsset?.id;
    if (!assetId) return;

    if (activeForecast && activeForecast.startsWith("processed-")) {
      const routeShort = activeForecast.split("processed-")[1];
      if (routeShort) {
        dispatch(setActiveProcessedModel({ assetId, routeShort }));
      }
    }

    return () => {
      dispatch(clearActiveProcessedModel(assetId));
    };
  }, [selectedAsset?.id, activeForecast]);


  useEffect(() => {
    // Power needs a wind curve. Fix up only the variation, via an updater so it sees the one the restore effect just set,
    // and save nothing: this effect still holds the previous asset's forecast, which would overwrite the new asset's saved choice.
    setForecastVariation((prev) => (prev === "Power" && !selectedAsset?.windCurve ? "Speed" : prev));

    // Cleared first so the previous asset's model data can't stand in for this one's while it loads.
    // Which forecast is selected is the default effect's job (above), not this one's.
    setProcessedModelData(null);
    if (selectedAsset?.processedModels?.items?.length > 0) {
      getProcessedModelS3(selectedAsset?.processedModels?.items);
    }

    setPrecipitation(null);
    setWind10(null);
    setWind80(null);
    setWindPower80Data(null);
    setSolarRadiation(null);
    setTemperature2m(null)
    setHumidity2m(null)
    setSoilMoisture(null)
    setSnowAcc(null)
    setDateTimeCycle(null);
    setGraphCastTemp(null);
    setGraphCastWind(null);
    setGraphCastPrecip(null);

    // No weather types are shown, so the weather data isn't needed
    if (!isWeatherHidden(selectedAsset?.displayedChannels)) {
      getForecasts();
      getShortForecast();
      getWind80();
    }
    setSolarEnergyInputs({ thefw: null, ohhfc: null });
    setSolarPowerData(null);

    setHighResPrecip(null);
    setHighResSolar(null);
    setHighResWind10(null);
    setHighResWind80(null);
    setHighResTemp(null);
    setHighResHumidity(null)
    // setHighResSoilMoisture(null)
  }, [selectedAsset]);

  const callSolarEnergyProductionAsync = async (datta) => {
    const res = await AssetGraphService.fetchSolarEnergyForecast(datta);
    setSolarPowerData(res);
  };

  const getProcessedModelS3 = async (items) => {
    try {
      // Log additionalDataPoints for all processedModels
      console.log("ProcessedModels:", items);
      items.forEach((model, index) => {
        if (model.additionalDataPoints && model.additionalDataPoints.length > 0) {
          console.log(
            `ProcessedModel [${index}] \"${model.label}\" (${model.routeShort}) additionalDataPoints:`,
            model.additionalDataPoints
          );
        }
      });

      const assetId = selectedAsset?.id;
      const finalprocessedModelData =
        await AssetGraphService.fetchProcessedModelData(items);
      if (isStaleFetch(assetId)) return;

      setProcessedModelData(finalprocessedModelData);
    } catch (e) {
      console.log("download processed Model error", e);
    }
  };

  // Fetch additional data sources when a processed model is selected
  useEffect(() => {
    const fetchAdditionalDataSources = async () => {
      if (!activeForecast || !activeForecast.includes("processed-")) {
        setAdditionalDataSources(null);
        return;
      }
 
      const pModelParameter = activeForecast.split("-")[1];
      const activeProcessedModel = selectedAsset?.processedModels?.items?.find(
        (pmodel) => pmodel.routeShort === pModelParameter
      );

      if (!activeProcessedModel?.additionalDataPoints || activeProcessedModel.additionalDataPoints.length === 0) {
        setAdditionalDataSources(null);
        return;
      }

      console.log("Fetching additional data sources for:", activeProcessedModel.label);

      try {
        const results =
          await AssetGraphService.fetchAdditionalDataSources(activeProcessedModel);
        console.log("Additional data sources fetched:", results || []);
        setAdditionalDataSources(results);
      } catch (error) {
        console.error("Error fetching additional data sources:", error);
        setAdditionalDataSources(null);
      }
    };

    fetchAdditionalDataSources();
  }, [activeForecast, selectedAsset]);

  useEffect(() => {
    if (
      solarEnergyInputs.ohhfc &&
      solarEnergyInputs.thefw &&
      selectedAsset.solarCurveId
    ) {
      const { solarCurve } = selectedAsset;
      const solarEnergyObj = {
        location: {
          latitude: selectedAsset.lat,
          longitude: selectedAsset.lng,
          name: selectedAsset.name, //Remove this
        },
        panel: {
          elevation: selectedAsset.elevation,
          pdc0: solarCurve.dcCap,
          pac0: solarCurve.acCap,
          gammaPdc: solarCurve.thermalLoss,
          panelEfficiency: solarCurve.panelEfficiency,
          minIrradiance: solarCurve.minIrradiance,
          tracking: solarCurve.trackingType.toLowerCase(),
        },
        trackingParams: {
          panelTilt: solarCurve.arrayTilt,
          panelAzimuth: solarCurve.arrayAzimuth,
          maxTilt: solarCurve.maxTilt,
          groundCoverRatio: solarCurve.groundCoverRatio,
        },
        weatherData: {
          ohhfc: solarEnergyInputs.ohhfc,
          ...solarEnergyInputs.thefw,
        },
      };
      callSolarEnergyProductionAsync(solarEnergyObj);
    }
  }, [solarEnergyInputs.ohhfc, solarEnergyInputs.thefw]);

  useEffect(() => {
    if (wind80 && selectedAsset?.windCurve?.curveData) {
      const windPowerData = AssetGraphService.buildWindPowerData(
        wind80,
        selectedAsset.windCurve.curveData
      );
      setWindPower80Data(windPowerData);
    }
  }, [wind80]);

  useEffect(() => {
    if (highResWind80?.Speed && selectedAsset?.windCurve?.curveData) {
      const highResWindPower = AssetGraphService.buildHighResWindPowerData(
        highResWind80.Speed,
        selectedAsset.windCurve.curveData
      );
      setHighResWindPower80Data(highResWindPower);
    }
  }, [highResWind80?.Speed]);

  useEffect(() => {
    const handleShortcut = (event) => {
      // Check if Ctrl key is pressed along with the 'K' key
      if (event.ctrlKey && event.key === "k") {
        event.preventDefault(); // Prevent the default action to avoid conflicts with browser shortcuts

        // Use the functional update form to toggle the timeZone state
        setTimeZone((currentTimeZone) => {
          // Determine and return the new state based on the current state
          const local = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const assetTimeZone = tzlookup(selectedAsset.lat, selectedAsset.lng);
          const timeZoneArray = ["UTC", local, assetTimeZone];
          const itemIndex = timeZoneArray.indexOf(currentTimeZone);
          if (itemIndex === 2 || itemIndex === -1) {
            return timeZoneArray[0];
          } else {
            return timeZoneArray[itemIndex + 1];
          }
        });
      }
    };
    // Attach the event listener for keydown when the component mounts
    window.addEventListener("keydown", handleShortcut);

    // Return a function to remove the event listener when the component unmounts
    return () => {
      window.removeEventListener("keydown", handleShortcut);
    };
  }, [selectedAsset]);


  // Which of Local / Asset / UTC the page is reading in
  const timeZoneMode = timeZone === "UTC" ? "UTC" : assetTimeZone && timeZone === assetTimeZone ? "asset" : "local";

  const handleSetTimeZone = (newTimeZone) => {
    setTimeZone(() => {
      if (newTimeZone === "asset") {
        return assetTimeZone || "UTC";
      }
      if (newTimeZone === "local") {
        return getLocalTimeZone();
      }
      return "UTC";
    });
  };

  // The variation a forecast opens on; null for a key this graph doesn't know
  const defaultVariationFor = (fc) => {
    switch (fc) {
      case "Precipitation":
      case "Temperature":
      case "Humidity":
      case "SoilMoisture":
      case "SnowAcc":
        return "Hourly";
      case "Wind10":
        return "Speed";
      case "Wind80":
        return selectedAsset?.windCurve ? "Power" : "Speed";
      case "Solar":
        return "sWave";
      default:
        if (fc?.includes("processed-")) return foreCastSchema[fc].variations[0].key;
        // Composed forecasts render one combined chart, so the variation is a single no-op key (see buildComposedForecastSchemaEntries).
        if (fc?.startsWith("composed-")) return foreCastSchema[fc]?.variations?.[0]?.key || "Combined";
        return null;
    }
  };

  const setForecastandVariation = (fc) => {
    const variation = defaultVariationFor(fc);
    if (variation) {
      setActiveForecast(fc);
      setForecastVariation(variation);
      saveAssetGraphSelection(selectedAsset?.id, fc, variation);
    }

    // TODO: Remove ReactGA and integrate PostHog for changeForecastParameter (usePostHogCapture).
    ReactGA.event({
      category: "Navigation",
      action: "Change Forecast",
      label: fc,
    });
  };

  const handleVariationChange = useCallback((fcVariationKey) => {
    setForecastVariation(fcVariationKey);
    saveAssetGraphSelection(selectedAsset?.id, activeForecast, fcVariationKey);

    // TODO: Remove ReactGA and integrate PostHog for changeForecastVariation (usePostHogCapture).
    ReactGA.event({
      category: "Navigation",
      action: "Change Forecast Variation",
      label: `${activeForecast}_${fcVariationKey}`,
    });
  }, [activeForecast, selectedAsset?.id]);

  // Makes `keys` (non-empty, top chart first) the stack: the top one is the main chart, so a different one on top hands it the page's controls
  const applyStack = (keys) => {
    const main = keys[0];
    if (main !== activeForecast) setForecastandVariation(main);
    // Creates the asset's record if this is its first change, or the extras save below has nothing to attach to
    else saveAssetGraphSelection(selectedAsset?.id, activeForecast, forecastVariation);
    const extras = keys.slice(1);
    setExtraForecasts(extras);
    saveAssetGraphExtras(selectedAsset?.id, extras);
  };

  const rememberOrder = (order) => {
    setChannelOrder(order);
    // Same as applyStack: the order needs the asset's record to exist
    saveAssetGraphSelection(selectedAsset?.id, activeForecast, forecastVariation);
    saveAssetGraphOrder(selectedAsset?.id, order);
  };

  // A drag in the picker moves any channel, ticked or not; the stack follows the order of the ticked ones
  const handleReorderChannels = (order) => {
    if (!order?.length) return;
    rememberOrder(order);
    const stack = order.filter((key) => selectedForecasts.includes(key));
    if (stack.length) applyStack(stack);
  };

  // A drag on the charts reorders the ticked channels among the slots they already hold in the picker's order
  const handleReorderSelection = (keys) => {
    if (!keys?.length) return;
    const next = [...orderedKeys];
    let slot = 0;
    orderedKeys.forEach((key, i) => {
      if (selectedForecasts.includes(key)) next[i] = keys[slot++];
    });
    rememberOrder(next);
    applyStack(keys);
  };

  // The picker hands back the full ticked list. The main forecast stays main while it is still ticked; unticking it promotes the first one left.
  const handleSelectionChange = (keys) => {
    if (!keys?.length) return;
    const main = keys.includes(activeForecast) ? activeForecast : keys[0];
    if (main !== activeForecast) setForecastandVariation(main);
    else saveAssetGraphSelection(selectedAsset?.id, activeForecast, forecastVariation);
    const extras = keys.filter((key) => key !== main);
    setExtraForecasts(extras);
    saveAssetGraphExtras(selectedAsset?.id, extras);
  };

  // withHeader={false}: the graph's own controls sit above this region, so a second row would double them
  const renderGraphLoading = () => (
    <div className="flex h-full flex-1 flex-col bg-eai-surface-card">
      <SkeletonChart withHeader={false} label="Loading forecast" />
    </div>
  );

  const renderNoForecasts = () => (
    <PageEmptyState
      className="h-full"
      title="No Custom View attached to this asset"
      description="Weather forecasts are hidden for this asset. Attach a Custom View or model in Edit Asset to see a forecast here."
    />
  );

  const renderTableFor = (forecast, variation, title) => (
    <AssetGraphTable
      activeForecast={forecast}
      forecastVariation={variation}
      interpolateChecked={interpolateChecked}
      processedModelData={processedModelData}
      composedForecastData={composedForecastData}
      precipitation={precipitation}
      wind10={wind10}
      wind80={wind80}
      windPower80Data={windPower80Data}
      solarPowerData={solarPowerData}
      solarRadiation={solarRadiation}
      temperature2m={temperature2m}
      humidity2m={humidity2m}
      soilMoisture={soilMoisture}
      snowAcc={snowAcc}
      highResPrecip={highResPrecip}
      highResWind10={highResWind10}
      highResWind80={highResWind80}
      highResWindPower80Data={highResWindPower80Data}
      highResSolar={highResSolar}
      highResTemp={highResTemp}
      highResHumidity={highResHumidity}
      timeZone={timeZone}
      renderLoading={renderGraphLoading}
      title={title}
    />
  );

  // The variation a stacked chart shows: the main chart's is the page's, an extra's is its own (default until changed)
  const variationFor = (key, index) => (index === 0 ? forecastVariation : extraVariations[key] ?? defaultVariationFor(key));

  const renderTable = () => {
    if (noForecastsToShow) return renderNoForecasts();
    if (!visibleExtras.length) return renderTableFor(activeForecast, forecastVariation);

    // Stacked in the same order as the charts, but fixed: reordering happens in the Graph view
    return (
      <div className="flex h-full min-h-0 flex-col overflow-y-auto">
        {selectedForecasts.map((key, index) => (
          <div key={key} className="min-h-[14rem] flex-1 shrink-0 border-t border-eai-border pb-6 pt-3 first:border-t-0 first:pt-0">
            {renderTableFor(key, variationFor(key, index), foreCastSchema?.[key]?.name || key)}
          </div>
        ))}
      </div>
    );
  };

  // One chart for a forecast. The main one owns the page's controls and extra data sources. Stacked, every chart instead registers its controls under its own key for its own toolbar.
  const renderGraphFor = (forecast, variation, isMain) => (
    <AssetGraphDisplay
      activeForecast={forecast}
      forecastVariation={variation}
      timeZone={timeZone}
      assetTimeZone={assetTimeZone}
      interpolateChecked={interpolateChecked}
      precipitation={precipitation}
      wind10={wind10}
      wind80={wind80}
      windPower80Data={windPower80Data}
      solarRadiation={solarRadiation}
      solarPowerData={solarPowerData}
      temperature2m={temperature2m}
      humidity2m={humidity2m}
      soilMoisture={soilMoisture}
      snowAcc={snowAcc}
      highResPrecip={highResPrecip}
      highResSolar={highResSolar}
      highResWind10={highResWind10}
      highResWind80={highResWind80}
      highResWindPower80Data={highResWindPower80Data}
      highResTemp={highResTemp}
      highResHumidity={highResHumidity}
      graphCastPrecip={graphCastPrecip}
      graphCastWind={graphCastWind}
      graphCastTemp={graphCastTemp}
      processedModelData={processedModelData}
      composedForecastData={composedForecastData}
      composedYAxisLabel={
        isMain
          ? composedYAxisLabel
          : forecast.startsWith("composed-")
          ? viewYAxisLabel(storedComposedForecasts?.[forecast.slice("composed-".length)] ?? resolveComposedForecast(selectedAsset, forecast))
          : null
      }
      additionalDataSources={isMain ? additionalDataSources : null}
      foreCastSchema={foreCastSchema}
      // Stacked, the zone is picked once in the top bar rather than in each chart's own time-range dialog
      onTimeZoneChange={visibleExtras.length > 0 ? undefined : handleSetTimeZone}
      onRegisterControls={visibleExtras.length > 0 ? registrarFor(forecast) : isMain ? handleRegisterControls : undefined}
      renderLoading={renderGraphLoading}
      hideGraphControls={true}
    />
  );

  const renderGraph = () => {
    if (noForecastsToShow) return renderNoForecasts();
    if (!visibleExtras.length) return renderGraphFor(activeForecast, forecastVariation, true);

    // Stacked: each chart gets a band of its own, draggable into a new order, and the stack scrolls once they no longer fit
    return (
      <SortableChartStack
        onReorder={handleReorderSelection}
        items={selectedForecasts.map((key, index) => ({
          key,
          label: foreCastSchema?.[key]?.name || key,
          node: renderGraphFor(key, variationFor(key, index), index === 0),
          controls: (
            <StackedChartControls
              label={foreCastSchema?.[key]?.name || key}
              variations={foreCastSchema?.[key]?.variations || []}
              variation={variationFor(key, index)}
              onVariationChange={(next) =>
                index === 0 ? handleVariationChange(next) : setExtraVariations((prev) => ({ ...prev, [key]: next }))
              }
              controls={controlsByKey[key]}
              onDownload={() => downloadCsvFor(key, variationFor(key, index))}
            />
          ),
        }))}
      />
    );
  };

  // CSV for one channel: the main one from the header's Download, or any stacked chart from its own toolbar
  const downloadCsvFor = (activeForecast, forecastVariation) => { // mike- refactor this to use callback
    if (!activeForecast) return;

    // Composed-forecast CSV uses a separate path: rows come from flattenComposedForecast, and the metadata/filename helpers live in assetGraphService as buildComposedCsvMetadata / buildComposedCsvFilename.
    if (activeForecast.startsWith("composed-")) {
      const forecastId = activeForecast.slice("composed-".length);
      const combined = composedForecastData[forecastId];
      const composedRows = flattenComposedForecast(combined);
      if (!composedRows.length) return;

      const composedCf = resolveComposedForecast(selectedAsset, activeForecast);
      const composedMetadata = AssetGraphService.buildComposedCsvMetadata({
        selectedAsset,
        composedForecast: composedCf,
      });
      const composedCsv = AssetGraphService.convertToCSV(composedRows, composedMetadata);
      const composedFilename = AssetGraphService.buildComposedCsvFilename({
        selectedAsset,
        composedForecast: composedCf,
      });
      AssetGraphService.downloadCsv(composedCsv, composedFilename);

      ReactGA.event({
        category: "Data",
        action: "Download CSV",
        label: `composed_${composedCf?.name || forecastId}`,
      });
      return;
    }

    const metadata = AssetGraphService.buildCsvMetadata({
      selectedAsset,
      activeForecast,
      forecastVariation,
      dateTimeCycle,
      processedModelData,
    });

    if (!metadata) {
      // solves race condition if not loaded
      return;
    }

    const csvData = AssetGraphService.buildFlatData({
      forecast: activeForecast,
      fcVariation: forecastVariation,
      interpolateChecked,
      processedModelData,
      precipitation,
      wind10,
      wind80,
      windPower80Data,
      solarPowerData,
      solarRadiation,
      temperature2m,
      humidity2m,
      soilMoisture,
      snowAcc,
      highResPrecip,
      highResWind10,
      highResWind80,
      highResWindPower80Data,
      highResSolar,
      highResTemp,
      highResHumidity,
    });

    if (!csvData || !Array.isArray(csvData) || csvData.length === 0) {
      return;
    }

    const csvStr = AssetGraphService.convertToCSV(csvData, metadata);
    const filename = AssetGraphService.buildCsvFilename({
      selectedAsset,
      activeForecast,
      forecastVariation,
      dateTimeCycle,
      interpolateChecked,
    });
    AssetGraphService.downloadCsv(csvStr, filename);

    // TODO: Remove ReactGA and integrate PostHog for downloadCSV (usePostHogCapture).
    ReactGA.event({
      category: "Data",
      action: "Download CSV",
      label: `${activeForecast}_${forecastVariation}`,
    });
  };
  const handleCSVDownload = () => downloadCsvFor(activeForecast, forecastVariation);

  // Behind a ref so the registration is stable: as a plain function handleCSVDownload had a new
  // identity every render, which re-ran this effect and re-registered the handler on each one
  const csvDownloadRef = useRef(handleCSVDownload);
  csvDownloadRef.current = handleCSVDownload;

  useEffect(() => {
    if (!onRegisterDownload) return;
    onRegisterDownload(() => csvDownloadRef.current());
    return () => onRegisterDownload(null);
  }, [onRegisterDownload]);

  const foreCastSchema = useMemo(() => {
    // Build the full forecast schema (includes composed-* entries from the asset's linkedComposedForecasts, regardless of fetch state)
    const fullSchema = buildForecastSchema(selectedAsset, processedModelData, composedForecastData);

    // Filter based on displayedChannels if present
    const filteredSchema = filterForecastSchema(fullSchema, selectedAsset?.displayedChannels);

    return filteredSchema;
  }, [windPower80Data, solarPowerData, selectedAsset, processedModelData, composedForecastData]);

  useEffect(() => {
    if (!onRegisterForecastControls) return;
    if (!foreCastSchema || !activeForecast) {
      onRegisterForecastControls(null);
      return;
    }
    onRegisterForecastControls({
      foreCastSchema,
      activeForecast,
      forecastVariation,
      onForecastSelect: setForecastandVariation,
      onVariationChange: handleVariationChange,
    });
    return () => onRegisterForecastControls(null);
  }, [
    onRegisterForecastControls,
    foreCastSchema,
    activeForecast,
    forecastVariation,
    handleVariationChange,
  ]);

  // Auto-select a valid forecast if the current one is filtered out or null
  useEffect(() => {
    // An empty schema (weather hidden, nothing attached) leaves the current weather type alone: the graph shows the
    // empty state either way, and keeping it lets the choice carry over to the next asset
    if (!foreCastSchema || Object.keys(foreCastSchema).length === 0) {
      return;
    }

    // If activeForecast is a processed model but processedModelData hasn't loaded yet, wait
    if (activeForecast && activeForecast.startsWith("processed-") && !processedModelData) {
      return; // Wait for processedModelData to load
    }

    // ONLY run smart default selection when: 1. No forecast is selected (null) 2. Current forecast is not available in the schema (was filtered out)
    if (!activeForecast || !foreCastSchema[activeForecast]) {
      const smartDefault = getSmartDefaultForecast(foreCastSchema, activeForecast || "Temperature", selectedAsset);

      // Only update if we found a valid forecast and it's different from current
      if (smartDefault && foreCastSchema[smartDefault] && smartDefault !== activeForecast) {
        setActiveForecast(smartDefault);

        // Set appropriate variation for the new forecast
        const defaultVariation = getDefaultVariation(smartDefault, foreCastSchema, selectedAsset);
        setForecastVariation(defaultVariation);
      }
    }
  }, [foreCastSchema, activeForecast, selectedAsset, processedModelData]);

  // Ensure valid variation when processedModelData loads or activeForecast changes to a processed model
  useEffect(() => {
    if (!activeForecast || !activeForecast.startsWith("processed-") || !processedModelData) {
      return;
    }

    const pModelParameter = activeForecast.split("-")[1];
    if (!processedModelData[pModelParameter]) {
      return;
    }

    const variations = processedModelData[pModelParameter].models ?? {};
    const variationKeys = Object.keys(variations);

    // If current variation doesn't exist in this processed model, set to first available
    if (!variations[forecastVariation]) {
      if (variationKeys.length > 0) {
        setForecastVariation(variationKeys[0]);
      }
    }
  }, [activeForecast, processedModelData, forecastVariation]);

  // Lazily fetch CombinedPreviewData when a composed-* forecast is selected. The sources come from the Custom Views
  // store when it has the forecast (it follows edits) and from the asset's links otherwise; the data is fetched again
  // when a source's settings (its weight, say) change, and otherwise kept. With no sources we bail rather than hammer
  // fetchCombinedPreviewData with an empty array.
  const storedComposedForecasts = useSelector((state: any) => state.composedForecasts?.forecasts);
  const composedSourcesSigRef = useRef<Record<string, string>>({});
  useEffect(() => {
    const assetId = selectedAsset?.id;
    [activeForecast, ...extraForecasts].forEach((key) => {
      if (!key?.startsWith("composed-")) return;
      const forecastId = key.slice("composed-".length);

      const cf = resolveComposedForecast(selectedAsset, key);
      const storedSources = storedComposedForecasts?.[forecastId]?.dataSources?.items;
      const sources = storedSources?.length ? storedSources : cf?.dataSources?.items;
      if (!sources?.length) return;
      const sig = sources.map((ds: any) => `${ds.id}:${ds.weight ?? 1}:${ds.updatedAt ?? ""}`).join("|");
      // The signature is recorded when a fetch starts, so a match means it has landed or is still in flight
      if (composedSourcesSigRef.current[forecastId] === sig) return;
      composedSourcesSigRef.current[forecastId] = sig;

      // Not cancelled by this effect re-running: with several Custom Views stacked, one landing re-runs it and would drop the others' fetches. A result for another asset is dropped instead.
      (async () => {
        try {
          const combined = await fetchCombinedPreviewData(sources);
          if (isStaleFetch(assetId)) {
            // Dropped, so forget the signature or coming back to this view would never fetch it again
            if (composedSourcesSigRef.current[forecastId] === sig) delete composedSourcesSigRef.current[forecastId];
            return;
          }
          setComposedForecastData((prev) => ({ ...prev, [forecastId]: combined }));
        } catch (err) {
          // Forget the signature so a later run can retry
          if (composedSourcesSigRef.current[forecastId] === sig) delete composedSourcesSigRef.current[forecastId];
          console.error("Failed to fetch composed forecast preview data:", err);
        }
      })();
    });
  }, [activeForecast, extraForecasts, selectedAsset, composedForecastData, storedComposedForecasts]);

  // The active Custom View's own y-axis label: the store's copy (it follows edits), else the asset's link. Read at render, so a
  // label change never refetches the data.
  const composedYAxisLabel = useMemo(() => {
    if (!activeForecast?.startsWith("composed-")) return null;
    const forecastId = activeForecast.slice("composed-".length);
    return viewYAxisLabel(storedComposedForecasts?.[forecastId] ?? resolveComposedForecast(selectedAsset, activeForecast));
  }, [activeForecast, storedComposedForecasts, selectedAsset]);

  // Every channel the asset offers in the user's order, then any the order doesn't mention yet in schema order
  const orderedKeys = useMemo(() => {
    const offered = Object.keys(foreCastSchema || {}).filter((key) => foreCastSchema[key]);
    return [...channelOrder.filter((key) => offered.includes(key)), ...offered.filter((key) => !channelOrder.includes(key))];
  }, [channelOrder, foreCastSchema]);

  // Only forecasts the asset still offers are stacked, never the main one twice, and in the picker's order
  const visibleExtras = useMemo(
    () => orderedKeys.filter((key) => key !== activeForecast && extraForecasts.includes(key)),
    [orderedKeys, extraForecasts, activeForecast]
  );
  const selectedForecasts = useMemo(
    () => (activeForecast ? [activeForecast, ...visibleExtras] : []),
    [activeForecast, visibleExtras]
  );
  const isMobile = useIsMobile();
  
  return (
    <div
      className={`flex flex-col h-full ${headerBar ? "gap-3" : ""} ${hideContainer ? "" : "bg-eai-surface-card border-x-1 border-eai-secondary-blue"}`}
    >
      {!hideHeader && (
        /* Even padding at both widths; as a bar it is a card in its own right, so the frame comes from eai-card */
        <div
          className={`${isMobile ? "gap-2" : ""} flex flex-none flex-col ${
            headerBar
              ? // Bar controls take the menu's 14px Outfit rather than the 12px toolbar rung, so the pills and the lists they open read alike
                "eai-card px-3 py-2 [&_.eai-btn-toolbar]:text-eai-control"
              : "px-3 pb-1 pt-3"
          }`}
        >
          {/* Desktop row: keep everything on a single line */}
          {!isMobile && (
            <AssetGraphHeaderDesktop
              leadingControl={leadingControl}
              viewSchema={viewSchema}
              displayView={displayView}
              onViewSelect={(view) => setDisplayView(view)}
              activeForecast={activeForecast}
              foreCastSchema={foreCastSchema}
              onForecastSelect={(foreCastName) => {
                setForecastandVariation(foreCastName);
              }}
              selectedForecasts={selectedForecasts}
              onSelectionChange={handleSelectionChange}
              onReorderSelection={handleReorderChannels}
              forecastOrder={orderedKeys}
              forecastVariation={forecastVariation}
              onVariationChange={handleVariationChange}
              hideChartTools={visibleExtras.length > 0}
              timeZoneMode={timeZoneMode}
              hasAssetTimeZone={Boolean(assetTimeZone)}
              onTimeZoneChange={handleSetTimeZone}
              onDownloadCSV={handleCSVDownload}
              onOpenTimeRange={graphControls.openTimeRange}
              onOpenDataset={graphControls.openDataset}
              selectedTimeRange={graphControls.selectedTimeRange}
              timeRangeLabel={TIME_RANGES.find(r => r.value === graphControls.selectedTimeRange)?.label || 'Full Range'}
            />
          )}

          {/* Mobile layout unchanged */}
          {isMobile && (
            <AssetGraphHeaderMobile
              leadingControl={leadingControl}
              activeForecast={activeForecast}
              foreCastSchema={foreCastSchema}
              onForecastSelect={(foreCastName) => {
                setForecastandVariation(foreCastName);
              }}
              selectedForecasts={selectedForecasts}
              onSelectionChange={handleSelectionChange}
              onReorderSelection={handleReorderChannels}
              forecastOrder={orderedKeys}
              forecastVariation={forecastVariation}
              onVariationChange={handleVariationChange}
              hideChartTools={visibleExtras.length > 0}
              timeZoneMode={timeZoneMode}
              hasAssetTimeZone={Boolean(assetTimeZone)}
              onTimeZoneChange={handleSetTimeZone}
              onOpenTimeRange={() =>
                graphControls.openTimeRange && graphControls.openTimeRange()
              }
              onOpenDataset={() =>
                graphControls.openDataset && graphControls.openDataset()
              }
            />
          )}
        </div>
      )}
      {/* A host panel rides inside the graph card so a peek rail covers the graph, not the bar; neither the row nor the column may create a stacking context — see PeekRail */}
      <div className={`flex-1 min-h-0 ${headerBar ? "eai-card overflow-hidden" : ""}`}>
        <div className={headerBar ? "relative flex h-full min-h-0" : "h-full"}>
          {graphLeadingPanel}
          <div className={headerBar ? "relative flex min-h-0 min-w-0 flex-1 flex-col" : "h-full"}>
            <AssetGraphDashboardView
              displayView={displayView}
              isMobile={isMobile}
              renderGraph={renderGraph}
              renderTable={renderTable}
              // Several stacked charts want the whole pane, so the map steps aside while they are up (Hybrid then reads as Graph)
              disableMapView={disableMapView || visibleExtras.length > 0}
              stacked={visibleExtras.length > 0}
              selectedAsset={selectedAsset}
              assetList={assetList}
              onAssetClick={(callbackAssetId) => {
                navigate(`/home/group/${groupid}/asset/${callbackAssetId}`);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetGraph;
